// Render a ReportDoc two ways: an .xlsx workbook, and printable A4 HTML.
// Both read the same model (see reportModel.ts) so paper and spreadsheet agree.
import { buildXlsx, safeSheetName, colLetter, STYLE, type Cell, type SheetSpec, type StyleId } from './xlsx.js';
import { printColumns, type ReportDoc, type ReportTable, type ReportCell } from './reportModel.js';

// ---- xlsx ---------------------------------------------------------------

const cellStyleFor = (align: string | undefined, wrap: boolean | undefined): StyleId =>
  wrap ? STYLE.cellWrap : align === 'center' ? STYLE.cellCenter : align === 'right' ? STYLE.cellNum : STYLE.cell;

/** One sheet per table: title, the document's header block, then the table. */
function sheetFor(doc: ReportDoc, table: ReportTable, usedNames: Set<string>): SheetSpec {
  const cols = table.columns;
  const rows: Cell[][] = [];

  rows.push([{ v: doc.title.toUpperCase(), s: STYLE.title }]);
  rows.push([{ v: table.title, s: STYLE.sectionHeading }]);
  for (const [k, v] of doc.meta) rows.push([{ v: k, s: STYLE.metaLabel }, { v, s: STYLE.metaValue }]);
  rows.push([]);

  const headerRow = rows.length + 1; // 1-based, for freeze + print titles
  rows.push(cols.map((c) => ({ v: c.header, s: STYLE.header })));

  if (table.rows.length) {
    for (const r of table.rows) {
      rows.push(cols.map((c, i) => ({ v: r[i] ?? null, s: cellStyleFor(c.align, c.wrap) })));
    }
  } else if (table.emptyNote) {
    rows.push([{ v: table.emptyNote, s: STYLE.cell }]);
  }

  if (table.notes?.length) {
    rows.push([]);
    for (const n of table.notes) rows.push([{ v: n, s: STYLE.note }]);
  }

  if (doc.signatories.length) {
    rows.push([]);
    rows.push([{ v: 'Prepared by', s: STYLE.metaLabel }, { v: doc.signatories[0]?.name || '', s: STYLE.metaValue }]);
    for (const s of doc.signatories.slice(1)) {
      rows.push([{ v: s.label, s: STYLE.metaLabel }, { v: s.name || '', s: STYLE.metaValue }]);
    }
  }

  // Unique sheet names: Excel refuses duplicates, and truncation to 31 chars can
  // collide two long titles.
  let name = safeSheetName(table.sheetName);
  if (usedNames.has(name)) {
    let n = 2;
    while (usedNames.has(safeSheetName(`${name.slice(0, 28)} ${n}`))) n++;
    name = safeSheetName(`${name.slice(0, 28)} ${n}`);
  }
  usedNames.add(name);

  return {
    name,
    rows,
    widths: cols.map((c) => c.width),
    freezeAboveRow: headerRow + 1,
    printTitleRows: [headerRow, headerRow],
    merges: [`A1:${colLetter(Math.max(0, cols.length - 1))}1`],
    landscape: table.landscape,
  };
}

/** Build a workbook from one or more documents — one sheet per table. */
export function docsToXlsx(docs: ReportDoc[]): Uint8Array {
  const used = new Set<string>();
  const sheets = docs.flatMap((doc) => doc.tables.map((t) => sheetFor(doc, t, used)));
  return buildXlsx(sheets);
}

// ---- printable A4 HTML --------------------------------------------------

const esc = (s: unknown): string =>
  String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]!);

const fmt = (v: ReportCell): string => (v === null || v === undefined ? '' : esc(v));

function tableHtml(table: ReportTable): string {
  const keep = printColumns(table);
  const total = keep.reduce((n, i) => n + table.columns[i].width, 0) || 1;
  const head = keep
    .map((i) => {
      const c = table.columns[i];
      return `<th style="width:${((100 * c.width) / total).toFixed(1)}%" class="a-${c.align ?? 'left'}">${esc(c.header)}</th>`;
    })
    .join('');
  const body = table.rows.length
    ? table.rows
        .map(
          (r) =>
            `<tr>${keep
              .map((i) => {
                const c = table.columns[i];
                return `<td class="a-${c.align ?? 'left'}${c.wrap ? ' wrap' : ''}">${fmt(r[i])}</td>`;
              })
              .join('')}</tr>`,
        )
        .join('')
    : `<tr><td colspan="${keep.length}" class="empty-cell">${esc(table.emptyNote ?? 'No data.')}</td></tr>`;

  const notes = table.notes?.length
    ? `<ul class="rp-notes">${table.notes.map((n) => `<li>${esc(n)}</li>`).join('')}</ul>`
    : '';
  const dropped = table.columns.filter((c) => c.wide).length;
  // Print must SAY what it dropped — a silently truncated table reads as complete.
  const droppedNote = dropped
    ? `<p class="rp-notes">${dropped} column${dropped === 1 ? '' : 's'} omitted here to fit A4 (${table.columns
        .filter((c) => c.wide)
        .map((c) => c.header)
        .slice(0, 4)
        .map(esc)
        .join(', ')}${dropped > 4 ? ', …' : ''}) — they are in the Excel export.</p>`
    : '';

  return `<section class="rp-section">
    <h2 class="rp-h2">${esc(table.title)}</h2>
    <table class="rp-table"><thead><tr>${head}</tr></thead><tbody>${body}</tbody></table>
    ${notes}${droppedNote}
  </section>`;
}

/**
 * One A4 document per ReportDoc: header block, each table on its own page, then
 * signature blocks. `page-break-before` on sections keeps tables from starting
 * halfway down a sheet.
 */
export function docToPrintHtml(doc: ReportDoc, opts: { first?: boolean } = {}): string {
  const meta = doc.meta
    .map(([k, v]) => `<div class="rp-kv"><span class="rp-k">${esc(k)}</span><span class="rp-v">${esc(v)}</span></div>`)
    .join('');
  const sigs = doc.signatories.length
    ? `<div class="rp-sigs">${doc.signatories
        .map(
          (s, i) => `<div class="rp-sig">
            <div class="rp-sig-line">${esc(s.name)}</div>
            <div class="rp-sig-label">${i === 0 ? 'Prepared by' : 'Noted by'} · ${esc(s.label)}</div>
          </div>`,
        )
        .join('')}</div>`
    : '';

  return `<article class="rp-doc${opts.first ? ' rp-first' : ''}">
    <header class="rp-head">
      <div class="rp-org">Republic of the Philippines · Department of Education</div>
      <h1 class="rp-h1">${esc(doc.title)}</h1>
      <div class="rp-meta">${meta}</div>
    </header>
    ${doc.tables.map(tableHtml).join('')}
    ${sigs}
  </article>`;
}
